import React from 'react';
import { useTranslation } from 'react-i18next';

const TechnologiesSection = () => {
    const { t } = useTranslation();

    const technologies = [
        {
            id: 'react',
            name: 'React JS / React Native',
            icon: '/assets/icons/react.svg',
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600',
            borderColor: 'border-blue-200'
        },
        {
            id: 'sqlserver',
            name: 'Microsoft SQL Server',
            icon: '/assets/icons/sql.svg',
            iconColor: 'text-red-600',
            borderColor: 'border-red-200'
        },
        {
            id: 'dotnet',
            name: 'Microsoft .NET Core',
            icon: '/assets/icons/dotnet.svg',
            iconColor: 'text-purple-600',
            borderColor: 'border-purple-200'
        },
        {
            id: 'angular',
            name: 'Angular',
            icon: '/assets/icons/angular.svg',
            iconColor: 'text-red-600',
            borderColor: 'border-red-200'
        },
        {
            id: 'oracle',
            name: 'Oracle',
            icon: '/assets/icons/oracle.svg',
            iconColor: 'text-red-600',
            borderColor: 'border-red-200'
        },
        {
            id: 'mongodb',
            name: 'MongoDB',
            icon: '/assets/icons/mongodb.svg',
            iconColor: 'text-green-600',
            borderColor: 'border-green-200'
        },
        {
            id: 'java',
            name: 'Java',
            icon: '/assets/icons/java.svg',
            iconColor: 'text-orange-600',
            borderColor: 'border-orange-200'
        }
        ,
        {
            id: 'python',
            name: 'Python',
            icon: '/assets/icons/python.svg',
            iconColor: 'text-yellow-600',
            borderColor: 'border-yellow-200'
        }
    ];

    return (
        <div className="py-8 sm:py-12 md:py-16 px-4 bg-white">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
                        {t('about.technology.title')}
                    </h2>
                    <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
                        {t('about.technology.description')}
                    </p>
                </div>

                {/* Technology Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 xl:gap-10">
                    {technologies.map((tech) => (
                        <div
                            key={tech.id}
                            className={`group bg-white border-2 ${tech.borderColor} rounded-xl p-4 sm:p-6 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300 hover:-translate-y-2 cursor-pointer relative overflow-hidden`}
                        >
                            {/* Icon Container */}
                            <div className="flex justify-center mb-4 sm:mb-6">
                                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl ${tech.bgColor} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 border-2 ${tech.borderColor} relative`}>
                                    {tech.id === 'oracle' ? (
                                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="48" height="48" rx="6" fill="#f80000" />
                                            <text x="24" y="30" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">ORACLE</text>
                                        </svg>
                                    ) : tech.id === 'dotnet' ? (
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="12" cy="12" r="11" fill="#512bd4" />
                                            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">.NET</text>
                                        </svg>
                                    ) : (
                                        <img
                                            src={tech.icon}
                                            alt={tech.name}
                                            className="w-8 h-8 sm:w-12 sm:h-12 object-contain"
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                maxWidth: '48px',
                                                maxHeight: '48px',
                                                objectFit: 'contain'
                                            }}
                                            onError={(e) => {
                                                // Hide the broken image and show fallback
                                                e.target.style.display = 'none';
                                                const fallback = e.target.parentElement.querySelector('.fallback-icon');
                                                if (fallback) fallback.style.display = 'flex';
                                            }}
                                            loading="lazy"
                                        />
                                    )}
                                    {/* Fallback icon */}
                                    <div
                                        className="fallback-icon w-12 h-12 flex items-center justify-center text-2xl font-bold text-gray-600"
                                        style={{ display: 'none' }}
                                    >
                                        {tech.name.charAt(0)}
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="text-center">
                                <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-gray-900 transition-colors duration-300">
                                    {t(`about.technology.technologies.${tech.id}.name`)}
                                </h3>
                                <p className="text-gray-600 leading-relaxed text-xs sm:text-sm group-hover:text-gray-700 transition-colors duration-300">
                                    {t(`about.technology.technologies.${tech.id}.description`)}
                                </p>
                            </div>

                            {/* Hover Effect Overlay */}
                            <div className={`absolute inset-0 rounded-xl ${tech.bgColor} opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none`}></div>
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default TechnologiesSection;
